import { g as getUsersBrowsingLocale, a as getCookie, s as setCookie } from "./utils-D8tYCXRD.js";
class LocalizationUi extends HTMLElement {
  constructor() {
    super();
    this.handleSubmit = (event) => {
      var _a;
      event.preventDefault();
      const countryInMarket = !((_a = this.marketInstances.find((market) => {
        return market.isoCode === this.selectedCountry;
      })) == null ? void 0 : _a.multiStore);
      if (!this.multiStore || countryInMarket) {
        this.setLocaleChoiceCookie();
        this.localizationForm.submit();
        return;
      }
      const redirectUrl = this.buildMultiStoreUrl();
      if (!redirectUrl) {
        return;
      }
      this.setLocaleChoiceCookie();
      window.location.href = redirectUrl;
    };
    this.storeInstances = [];
    this.marketInstances = [];
    this.multiStore = false;
    this.currentCountry = "US";
    this.currentLanguage = "EN";
    this.persistDays = 28;
  }
  connectedCallback() {
    this.getComponentAttributes();
    this.cacheDomElements();
    this.setEventListeners();
    if (this.multiStore) {
      this.normalizeLocaleData();
      this.updateCountrySelect();
    }
    this.checkUserLocaleOnLoad();
  }
  async checkUserLocaleOnLoad() {
    const locale = await getUsersBrowsingLocale();
    if (!locale) {
      return;
    }
    const currentLocale = locale.detected_values.country.handle;
    const currentCountryName = locale.detected_values.country.name;
    if (currentLocale === this.currentCountry) {
      return;
    }
    const choice = getCookie("localization-choice");
    if (choice) {
      return;
    }
    const hasSupportedLocale = this.marketInstances.find(
      (market) => market.isoCode === currentLocale
    );
    if (!hasSupportedLocale) {
      return;
    }
    if (!currentCountryName) {
      return;
    }
    this.showChoicesUi(currentCountryName);
  }
  showChoicesUi(currentCountryName) {
    const countryTitle = this.querySelector(
      ".localization-ui__title"
    );
    const countryText = this.querySelector(
      ".localization-ui__text"
    );
    if (countryTitle) {
      if (currentCountryName === "United Kingdom" || currentCountryName === "United States") {
        countryTitle.textContent = `${countryTitle.textContent} the ${currentCountryName}`;
      } else {
        countryTitle.textContent = `${countryTitle.textContent} ${currentCountryName}`;
      }
    }
    if (countryText) {
      const textString = countryText.innerText.replace(
        "[replace-here]",
        currentCountryName
      );
      countryText.innerText = textString;
    }
    if (this.shopHereButton && this.shopHereButton.textContent !== null) {
      this.shopHereButton.textContent = this.shopHereButton.textContent.replace(
        "[replace-here]",
        (this == null ? void 0 : this.currentCountry) || "GB"
      );
    }
    this.localizationForm.classList.add("visually-hidden");
    this.choicesForm.classList.remove("visually-hidden");
    document.dispatchEvent(new CustomEvent(`modal:show:localization`));
  }
  showSettingsUi() {
    this.localizationForm.classList.remove("visually-hidden");
    this.choicesForm.classList.add("visually-hidden");
  }
  /**
   * Normalizes the store instances data to match the market instances data.
   * If we have multi-store instances we need the data to match a structure for
   * native markets to improve how we handle user selections on the ui elements.
   */
  normalizeLocaleData() {
    var _a;
    const normalizedStores = [];
    for (const store of this.storeInstances) {
      const supportedCountryCodes = store.supportedCountries.map(
        (country) => country.split(":")[0]
      );
      for (const supportedCountryCode of supportedCountryCodes) {
        const normalizedStore = {
          name: ((_a = store.supportedCountries.find((country) => country.split(":")[0] === supportedCountryCode)) == null ? void 0 : _a.split(":")[1]) || "",
          isoCode: supportedCountryCode,
          multiStore: true,
          publicUrl: store.publicUrl,
          shopifyUrl: store.shopifyUrl,
          isBaseCountry: supportedCountryCode === store.baseCountry,
          languages: this.getNormalizedLanguages(supportedCountryCode, store)
        };
        normalizedStores.push(normalizedStore);
      }
    }
    this.marketInstances = this.marketInstances.concat(normalizedStores);
  }
  /**
   * Gets the languages supported on each market of a multi-store instance.
   * Uses the supported locales heavily, so when merchandising these in the
   * metaobject it is important to list all supported locales else the
   * country/language relationship won't be accurate from this function.
   * @param {CountryCode} countryCode - The country code to get the languages for.
   * @param {StoreInstance} store - The store instance to get the languages from.
   * @returns {MarketInstance.languages[]}
   */
  getNormalizedLanguages(countryCode, store) {
    const languages = [];
    const supportedLanguageCodes = store.supportedLocales.filter((locale) => locale.endsWith(countryCode)).map((locale) => locale.split("-")[0]);
    for (const languageCode of supportedLanguageCodes) {
      const language = store.supportedLanguages.find(
        (lang) => lang.split(":")[0] === languageCode
      );
      if (!language) {
        continue;
      }
      languages.push({
        isoCode: languageCode,
        name: language.split(":")[1]
      });
    }
    return languages;
  }
  cacheDomElements() {
    const form = this.querySelector(".localization-ui__form");
    if (form) {
      this.localizationForm = form;
    }
    const choicesForm = this.querySelector('[js-localization-ui="choice"]');
    if (choicesForm) {
      this.choicesForm = choicesForm;
    }
    const countrySelect = this.querySelector(
      '[js-localization-ui="country-select"]'
    );
    if (countrySelect) {
      this.countrySelect = countrySelect;
    }
    const languageSelect = this.querySelector(
      '[js-localization-ui="language-select"]'
    );
    if (languageSelect) {
      this.languageSelect = languageSelect;
    }
    const submitButton = this.querySelector(
      '[js-localization-ui="submit"]'
    );
    if (submitButton) {
      this.submitButton = submitButton;
    }
    const shopHereButton = this.querySelector(
      '[js-localization-ui="shop-here"]'
    );
    if (shopHereButton) {
      this.shopHereButton = shopHereButton;
    }
    const changeRegionButton = this.querySelector(
      '[js-localization-ui="change-region"]'
    );
    if (changeRegionButton) {
      this.changeRegionButton = changeRegionButton;
    }
  }
  getComponentAttributes() {
    var _a, _b;
    const instances = this.getAttribute(
      "setting-store-instances"
    );
    this.multiStore = this.getAttribute("setting-multi-store") === "true";
    if (instances) {
      this.storeInstances = (_a = JSON.parse(instances)) == null ? void 0 : _a.instances;
    }
    const markets = this.getAttribute("setting-store-markets");
    if (markets) {
      this.marketInstances = (_b = JSON.parse(markets)) == null ? void 0 : _b.markets;
    }
    this.currentCountry = this.getAttribute("setting-current-country");
    this.selectedCountry = this.currentCountry;
    this.currentLanguage = this.getAttribute("setting-current-language");
    this.selectedLanguage = this.currentLanguage;
    this.persistDays = parseInt(
      this.getAttribute("setting-persist-days") || "28"
    );
  }
  /**
   * Updates the country select dropdown with the list of countries.
   */
  updateCountrySelect() {
    if (!this.countrySelect) {
      return;
    }
    this.countrySelect.innerHTML = "";
    const defaultOption = document.createElement("option");
    defaultOption.value = "";
    defaultOption.disabled = true;
    defaultOption.textContent = window.themeVars.strings.localization.country_default;
    this.countrySelect.appendChild(defaultOption);
    for (const country of this.marketInstances) {
      const option = document.createElement("option");
      option.value = country.isoCode;
      option.textContent = `${country.name} - ${country.isoCode}`;
      option.selected = country.isoCode === this.currentCountry;
      option.dataset.multiStore = Boolean(country == null ? void 0 : country.multiStore).toString();
      this.countrySelect.appendChild(option);
    }
  }
  setEventListeners() {
    if (this.countrySelect) {
      this.countrySelect.addEventListener("change", (event) => {
        const target = event.target;
        const option = target.options[target.selectedIndex];
        this.selectedCountry = option.value;
        this.updateLanguageSelect();
      });
    }
    if (this.languageSelect) {
      this.languageSelect.addEventListener("change", (event) => {
        const target = event.target;
        const option = target.options[target.selectedIndex];
        this.selectedLanguage = option.value;
      });
    }
    if (this.submitButton) {
      this.submitButton.addEventListener("click", this.handleSubmit);
    }
    if (this.shopHereButton) {
      this.shopHereButton.addEventListener("click", () => {
        document.dispatchEvent(new CustomEvent(`modal:hide:localization`));
      });
    }
    if (this.changeRegionButton) {
      this.changeRegionButton.addEventListener("click", () => {
        this.showSettingsUi();
      });
    }
    document.addEventListener(`modal:closed:localization`, () => {
      this.setLocaleChoiceCookie();
    });
  }
  setLocaleChoiceCookie() {
    setCookie(
      "localization-choice",
      `
      ${this.selectedLanguage ? this.selectedLanguage : this.currentLanguage}-${this.selectedCountry ? this.selectedCountry : this.currentCountry}`,
      this.persistDays
    );
  }
  /**
   * Handles the form submission for multi-store instances.
   * @returns {void}
   */
  buildMultiStoreUrl() {
    const selectedMarket = this.marketInstances.find(
      (market) => market.isoCode === this.selectedCountry
    );
    const selectedStoreInstance = this.storeInstances.find(
      (store) => store.supportedCountries.includes(
        `${this.selectedCountry}:${selectedMarket == null ? void 0 : selectedMarket.name}`
      )
    );
    if (!selectedMarket || !selectedStoreInstance) {
      return;
    }
    const currentUrl = new URL(window.location.href);
    const selectedMarketUrl = new URL((selectedMarket == null ? void 0 : selectedMarket.publicUrl) || "");
    if (currentUrl.origin === selectedMarketUrl.origin) {
      this.localizationForm.submit();
      return;
    }
    const selectedLocale = this.buildLocale(
      selectedStoreInstance,
      this.selectedCountry || "gb",
      this.selectedLanguage || "en"
    );
    return this.buildRedirectUrl(selectedMarketUrl, currentUrl, selectedLocale);
  }
  /**
   *  Builds the redirect URL for the multi-store instance.
   * @param {URL} selectedMarketUrl - The URL of the selected market.
   * @param {URL} currentUrl - The URL of the current page.
   * @param {String} locale - The locale to use in the redirect URL.
   * @returns {String} - The redirect URL.
   */
  buildRedirectUrl(selectedMarketUrl, currentUrl, locale) {
    const currentPath = this.removeLocaleFromPath(currentUrl.pathname);
    const selectedOrigin = selectedMarketUrl.origin;
    const currentSearch = currentUrl.search;
    const currentHash = currentUrl.hash;
    const newUrl = new URL(selectedOrigin);
    newUrl.pathname = locale ? `/${locale}/${currentPath}` : currentPath;
    newUrl.search = currentSearch;
    newUrl.hash = currentHash;
    return newUrl.toString();
  }
  /**
   *  Builds the locale for the multi-store instance.
   * @param {StoreInstance} storeInstance - The store instance to build the locale for.
   * @param {string} selectedCountry - The selected country.
   * @param {string} selectedLanguage - The selected language.
   * @returns {string} - The locale.
   */
  buildLocale(storeInstance, selectedCountry, selectedLanguage) {
    let locale = "";
    if (selectedCountry && selectedCountry !== storeInstance.baseCountry) {
      if (selectedLanguage) {
        locale = `${selectedLanguage.toLowerCase()}-${selectedCountry.toLowerCase()}`;
      }
    } else if (selectedLanguage && selectedLanguage !== storeInstance.baseLanguage) {
      locale = selectedLanguage.toLowerCase();
    }
    return locale;
  }
  removeLocaleFromPath(path) {
    const pattern = /^(\/?)(?:[a-z]{2}-[a-z]{2}|[a-z]{2})(\/|$)/;
    return path.replace(pattern, "");
  }
  /**
   * Updates the language select dropdown with the list of languages for the
   * selected country.
   */
  updateLanguageSelect() {
    const marketInstance = this.marketInstances.find(
      (market) => market.isoCode === this.selectedCountry
    );
    if (!marketInstance || !this.languageSelect) {
      return;
    }
    this.languageSelect.innerHTML = "";
    const defaultOption = document.createElement("option");
    defaultOption.value = "";
    defaultOption.disabled = true;
    defaultOption.textContent = window.themeVars.strings.localization.language_default;
    this.languageSelect.appendChild(defaultOption);
    if (!marketInstance.languages.find(
      (language) => language.isoCode === this.selectedLanguage
    )) {
      this.selectedLanguage = marketInstance.languages[0].isoCode;
    }
    for (const language of marketInstance.languages) {
      const option = document.createElement("option");
      option.value = language.isoCode;
      option.textContent = language.name;
      option.selected = language.isoCode === this.selectedLanguage;
      this.languageSelect.appendChild(option);
    }
  }
}
customElements.define("uwp-localization-ui", LocalizationUi);
